import { useEffect, useState } from 'react';
import {
  Page, Card, Button, Banner, Text, TextField, Checkbox,
  Layout, BlockStack, InlineStack, Box, Spinner, DataTable, Pagination, InlineGrid,
} from '@shopify/polaris';
import { MegaphoneIcon } from '@shopify/polaris-icons';
import { useSocialProof } from '../../hooks/useSocialProof';
import type { SocialProofSettings } from '../../api/socialProof';
import { StatCard } from '../shared/StatCard';

function LivePreview({ settings }: { settings: SocialProofSettings }) {
  const who = settings.show_customer_name ? `Sarah${settings.show_location ? ' from Austin' : ''}` : 'Someone';

  return (
    <BlockStack gap="200">
      <div style={{ background: '#f6f6f7', borderRadius: 8, padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, maxWidth: 320, padding: '13px 16px 13px 13px', borderRadius: 14, background: '#fff', border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 12px 32px rgba(0,0,0,0.16)', fontSize: 13, color: '#1d1d1d', lineHeight: 1.4 }}>
          <div style={{ width: 42, height: 42, borderRadius: 10, background: 'linear-gradient(135deg, #ecfdf5, #d1fae5)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>
            🛍️
          </div>
          <div>
            <div>{who} recently bought <strong>Wireless Earbuds Pro</strong></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 3, fontSize: 11.5, color: '#10b981', fontWeight: 600 }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#10b981', flexShrink: 0 }} />
              Verified purchase
            </div>
          </div>
        </div>
      </div>
      <Text as="p" variant="bodySm" tone="subdued">
        Pinned to the bottom-left corner of your storefront, built from real orders — visible for{' '}
        {settings.display_duration_seconds}s, {settings.display_interval_seconds}s apart.
      </Text>
    </BlockStack>
  );
}

export function SocialProofPanel() {
  const {
    settings, events, eventsTotal, eventsPage, eventsLastPage,
    loading, saving, error, fetchSettings, save, fetchEvents, setError,
  } = useSocialProof();

  const [form, setForm] = useState<SocialProofSettings | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => { fetchSettings(); fetchEvents(1); }, [fetchSettings, fetchEvents]);
  useEffect(() => { if (settings) setForm(settings); }, [settings]);

  if (loading) {
    return (
      <Page fullWidth title="Social Proof Popups">
        <Layout>
          <Layout.Section>
            <InlineStack align="center"><Spinner /></InlineStack>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  if (!form) {
    return (
      <Page fullWidth title="Social Proof Popups">
        <Layout>
          <Layout.Section>
            <Banner tone="critical" action={{ content: 'Retry', onAction: fetchSettings }}>
              <p>{error ?? 'Failed to load social proof settings.'}</p>
            </Banner>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  const handleSave = async () => {
    setSaved(false);
    const result = await save(form);
    if (result) setSaved(true);
  };

  return (
    <Page fullWidth
      title="Social Proof Popups"
      subtitle='Show shoppers a live "Sarah from Austin just bought this" toast, built from your own real order activity'
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            {error && (
              <Banner tone="critical" onDismiss={() => setError(null)}>
                <p>{error}</p>
              </Banner>
            )}
            {saved && (
              <Banner tone="success" onDismiss={() => setSaved(false)}>
                <p>Settings saved. Changes appear on your storefront within about 30 seconds.</p>
              </Banner>
            )}

            <InlineGrid columns={{ xs: 1, sm: 2 }} gap="300">
              <StatCard label="Recent Purchase Events" value={String(eventsTotal)} icon={MegaphoneIcon} iconColor="teal" />
              <StatCard
                label="Widget Status"
                value={form.enabled ? 'Enabled' : 'Disabled'}
                icon={MegaphoneIcon}
                iconColor={form.enabled ? 'green' : 'indigo'}
              />
            </InlineGrid>

            <Card>
              <BlockStack gap="400">
                <Checkbox
                  label="Show recent-purchase popups on my storefront"
                  checked={form.enabled}
                  onChange={(v) => setForm({ ...form, enabled: v })}
                />
                <Checkbox
                  label="Show the customer's first name"
                  checked={form.show_customer_name}
                  onChange={(v) => setForm({ ...form, show_customer_name: v })}
                />
                <Checkbox
                  label="Show the customer's city"
                  checked={form.show_location}
                  onChange={(v) => setForm({ ...form, show_location: v })}
                />

                <InlineStack gap="300">
                  <Box minWidth="200px">
                    <TextField
                      label="Visible for (seconds)"
                      type="number"
                      autoComplete="off"
                      value={String(form.display_duration_seconds)}
                      onChange={(v) => setForm({ ...form, display_duration_seconds: Number(v) || 5 })}
                    />
                  </Box>
                  <Box minWidth="200px">
                    <TextField
                      label="Time between popups (seconds)"
                      type="number"
                      autoComplete="off"
                      value={String(form.display_interval_seconds)}
                      onChange={(v) => setForm({ ...form, display_interval_seconds: Number(v) || 8 })}
                    />
                  </Box>
                </InlineStack>

                <Box>
                  <Button variant="primary" loading={saving} onClick={handleSave}>Save Settings</Button>
                </Box>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">Live Preview</Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  This is exactly what shoppers see pop up on your storefront.
                </Text>
                <LivePreview settings={form} />
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">Recent Events</Text>
                {events.length === 0 ? (
                  <Text as="p" tone="subdued">No purchase events recorded yet — these appear automatically as orders come in.</Text>
                ) : (
                  <DataTable
                    columnContentTypes={['text', 'text', 'text']}
                    headings={['Customer', 'Product', 'When']}
                    rows={events.map((e) => [
                      [e.customer_first_name, e.customer_location].filter(Boolean).join(' from ') || 'A customer',
                      e.product_title,
                      e.occurred_at ? new Date(e.occurred_at).toLocaleString() : '—',
                    ])}
                  />
                )}
                {eventsLastPage > 1 && (
                  <InlineStack align="center">
                    <Pagination
                      hasPrevious={eventsPage > 1}
                      onPrevious={() => fetchEvents(eventsPage - 1)}
                      hasNext={eventsPage < eventsLastPage}
                      onNext={() => fetchEvents(eventsPage + 1)}
                    />
                  </InlineStack>
                )}
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="200">
                <Text as="h2" variant="headingMd">One more step: enable it in your theme</Text>
                <Text as="p" tone="subdued">
                  Go to <strong>Online Store &rarr; Themes &rarr; Customize &rarr; App embeds</strong>, then enable{' '}
                  <strong>Social Proof Popups</strong>. This is a one-time toggle per theme.
                </Text>
              </BlockStack>
            </Card>
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
